import React, { useState } from 'react';
import { Progress } from 'antd';
import { DownloadOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { useDownloadProgress } from '../contexts/DownloadProgressContext';
import './GlobalDownloadProgress.css';

const GlobalDownloadProgress: React.FC = () => {
  const { downloadProgress, isDownloading } = useDownloadProgress();
  const [isExpanded, setIsExpanded] = useState(true); // Expanded by default; user can collapse

  if (!downloadProgress || !isDownloading) {
    return null;
  }

  const isStreaming = downloadProgress.stage === 'streaming';
  const bytesLoaded = downloadProgress.bytesLoaded ?? 0;
  const bytesMB = (bytesLoaded / (1024 * 1024)).toFixed(2);

  const getProgressPercent = (): number => {
    if (downloadProgress.stage === 'generating') {
      return 100;
    }
    if (isStreaming && bytesLoaded > 0) {
      // ~2% per MB received (cap at 95% until response completes)
      const percentPerMB = 2;
      const mb = bytesLoaded / (1024 * 1024);
      return Math.min(95, Math.floor(mb * percentPerMB));
    }
    if (downloadProgress.totalPages > 0) {
      return Math.round((downloadProgress.currentPage / downloadProgress.totalPages) * 100);
    }
    return 0;
  };

  const getStatusText = (): string => {
    if (downloadProgress.stage === 'generating') {
      return 'Complete';
    }
    if (isStreaming) {
      return bytesLoaded > 0 ? `${bytesMB} MB received` : 'Exporting members...';
    }
    if (downloadProgress.stage === 'fetching') {
      return downloadProgress.totalPages > 0
        ? `Page ${downloadProgress.currentPage} of ${downloadProgress.totalPages}`
        : 'Fetching members...';
    }
    return 'Generating CSV...';
  };

  const getStageTitle = (): string => {
    if (downloadProgress.stage === 'generating') return 'Download Complete';
    if (isStreaming) return 'Exporting Members';
    if (downloadProgress.stage === 'fetching') return 'Fetching Members';
    return 'Generating CSV';
  };

  const progressPercent = getProgressPercent();

  return (
    <div className={`global-download-progress ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {isExpanded ? (
        // Expanded view: Circular progress with details
        <div className="progress-expanded-container">
          <div className="progress-header">
            <div className="progress-title">
              <DownloadOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
              <span>{getStageTitle()}</span>
            </div>
            <button 
              className="collapse-button"
              onClick={() => setIsExpanded(false)}
              aria-label="Collapse"
            >
              <DownOutlined className="collapse-icon" />
            </button>
          </div>
          
          <div className="progress-circle-wrapper">
            <Progress
              type="circle"
              percent={progressPercent}
              size={120}
              strokeWidth={8}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              format={() => (
                <div className="progress-content">
                  <div className="progress-percent">{progressPercent}%</div>
                </div>
              )}
              status={progressPercent >= 100 ? 'success' : 'active'}
            />
          </div>
          
          <div className="progress-details">
            <div className="progress-detail-item">
              <span className="detail-label">Status:</span>
              <span className="detail-value">{getStatusText()}</span>
            </div>
            {!isStreaming && (
              <div className="progress-detail-item">
                <span className="detail-label">Members:</span>
                <span className="detail-value">{downloadProgress.membersCount.toLocaleString()}</span>
              </div>
            )}
            {downloadProgress.stage === 'fetching' && downloadProgress.totalPages > 0 && (
              <div className="progress-detail-item">
                <span className="detail-label">Progress:</span>
                <span className="detail-value">
                  {downloadProgress.currentPage} / {downloadProgress.totalPages} pages
                </span>
              </div>
            )}
            {isStreaming && (
              <div className="progress-detail-item progress-hint">
                <span className="detail-value">You can switch pages – download will continue.</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Collapsed view: no percentage bar when streaming (no real total); show only dynamic bytes
        <div className="progress-collapsed-container" onClick={() => setIsExpanded(true)}>
          {!isStreaming && (
            <div className="collapsed-progress-bar">
              <Progress
                percent={progressPercent}
                showInfo={false}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
                status="active"
              />
            </div>
          )}
          <div className="collapsed-info">
            <DownloadOutlined style={{ fontSize: '14px', marginRight: '6px' }} />
            <span className="collapsed-percent">
              {isStreaming
                ? (bytesLoaded > 0 ? `${bytesMB} MB received` : 'Exporting...')
                : `${progressPercent}%`}
            </span>
            <button 
              className="expand-button"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
              }}
              aria-label="Expand"
            >
              <UpOutlined className="expand-icon" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalDownloadProgress;
